/**
 * PageHeader — Standardised header for content pages.
 *
 * Used by feature, project, and mandate pages to give each route a consistent
 * frame:
 *
 *   [breadcrumb]
 *   [icon] Title                       [primary action] [actions...]
 *   subtitle / description
 *   [tabs: Aperçu | Documents | … ]
 *
 * Below the header, pages render their own content as <Outlet/> or children.
 */

import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'

export interface PageBreadcrumb {
  label: string
  href?: string
}

export interface PageTab {
  id: string
  label: string
  href: string
  icon?: LucideIcon
  count?: number
}

interface PageHeaderProps {
  title: string
  subtitle?: string
  icon?: LucideIcon
  accent?: 'blue' | 'amber' | 'green' | 'purple' | 'red' | 'teal' | 'indigo' | 'slate'
  badge?: string
  breadcrumb?: PageBreadcrumb[]
  tabs?: PageTab[]
  actions?: React.ReactNode
  className?: string
}

const ACCENT_BG: Record<NonNullable<PageHeaderProps['accent']>, string> = {
  blue: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  amber: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  green: 'bg-green-500/15 text-green-600 dark:text-green-400',
  purple: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
  red: 'bg-red-500/15 text-red-600 dark:text-red-400',
  teal: 'bg-teal-500/15 text-teal-600 dark:text-teal-400',
  indigo: 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400',
  slate: 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
}

export function PageHeader({
  title,
  subtitle,
  icon: Icon,
  accent = 'blue',
  badge,
  breadcrumb,
  tabs,
  actions,
  className,
}: PageHeaderProps) {
  const location = useLocation()

  return (
    <header className={cn('flex flex-col gap-3 pb-4 border-b mb-4', className)}>
      {breadcrumb && breadcrumb.length > 0 && (
        <ol className="flex items-center gap-1 text-[11px] text-muted-foreground" aria-label="Breadcrumb">
          {breadcrumb.map((crumb, idx) => (
            <li key={idx} className="flex items-center gap-1">
              {idx > 0 && <ChevronRight className="h-3 w-3 opacity-50" />}
              {crumb.href ? (
                <Link to={crumb.href} className="hover:text-foreground hover:underline">
                  {crumb.label}
                </Link>
              ) : (
                <span className={cn(idx === breadcrumb.length - 1 && 'text-foreground font-medium')}>
                  {crumb.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}

      <div className="flex items-start gap-3">
        {Icon && (
          <span className={cn('flex h-10 w-10 items-center justify-center rounded-xl shrink-0', ACCENT_BG[accent])}>
            <Icon className="h-5 w-5" />
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-[20px] font-extrabold leading-tight text-foreground">{title}</h1>
            {badge && <Badge variant="secondary" className="text-[10px] h-5">{badge}</Badge>}
          </div>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>
          )}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>

      {tabs && tabs.length > 0 && (
        <nav aria-label="Page sections" className="flex items-center gap-1 -mb-4 overflow-x-auto scrollbar-none">
          {tabs.map((tab) => {
            const isActive =
              location.pathname === tab.href ||
              location.pathname.startsWith(tab.href + '/')
            const TabIcon = tab.icon
            return (
              <Link
                key={tab.id}
                to={tab.href}
                className={cn(
                  'flex items-center gap-1.5 rounded-t-lg border-b-2 px-3 py-2 text-[12px] font-semibold whitespace-nowrap transition-colors',
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/40',
                )}
              >
                {TabIcon && <TabIcon className="h-3.5 w-3.5" />}
                <span>{tab.label}</span>
                {typeof tab.count === 'number' && (
                  <span
                    className={cn(
                      'rounded-full px-1.5 py-0 text-[9px] font-bold',
                      isActive ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {tab.count}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>
      )}
    </header>
  )
}
